import {
  Disclaimer,
  displayCentsInDollars,
  Div,
  H3,
  H5,
  lightTheme,
  LocationServiceValue,
  Modal,
  P,
  Separator,
} from '@vgrubs/components';
import { periodLabel } from '../helpers/serviceValueLines';
import {
  clientPartInfo,
  partColor,
  partSegments,
  partsTotal,
  ValuePartKey,
  valueParts,
} from '../helpers/valueParts';
import { BrandValueCard } from './BrandValueCard';
import { MoneyLine } from './MoneyLine';
import { ValueBar } from './ValueBar';

/** The summary names the service; a brand card only needs the part. */
const SUMMARY_LABEL: Partial<Record<ValuePartKey, string>> = {
  payout: 'Brands payout',
  recoveries: 'Revenue Recovery',
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  value: LocationServiceValue;
};

/**
 * Everything behind "Client Made & Saved": the total and the bar of what it
 * is made of, its three parts, then every brand as a card - biggest first,
 * each bar on the same scale, so where the money comes from shows before a
 * single number is read.
 */
export const ClientValueModal = ({ isOpen, onClose, value }: Props) => {
  const { client } = value;
  const parts = valueParts(client);
  const info = clientPartInfo(client);

  // Biggest first: the backend sends them by brand id, which says nothing.
  const brands = client.brands
    .map((brand) => ({
      brand,
      total: partsTotal(valueParts(brand, { withPayout: brand.virtual })),
    }))
    .sort((a, b) => b.total - a.total);
  const brandScale = brands[0]?.total ?? 0;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header="Client Made & Saved"
      subtitle={periodLabel(value.from, value.to)}
      textAlign="left"
      width="640px"
    >
      <P $m="0">Made & saved in total</P>
      <H3 $m="0 0 12px" $color={lightTheme.green}>
        {displayCentsInDollars(client.total)}
      </H3>
      <ValueBar
        segments={partSegments(parts)}
        scale={partsTotal(parts)}
        height={10}
      />

      <Div $flex $column $gap="8px" $mt="16">
        {parts.map((part) => (
          <MoneyLine
            key={part.key}
            dot={partColor(part.key)}
            label={SUMMARY_LABEL[part.key] ?? part.label}
            info={info[part.key]}
            amount={part.amount}
          />
        ))}
      </Div>

      <Separator $m="20px 0 16px" />

      <H5 $m="0 0 12px">By brand</H5>
      {brands.length === 0 && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          Nothing in this period.
        </Disclaimer>
      )}
      <Div $flex $column $gap="8px">
        {brands.map(({ brand }) => (
          <BrandValueCard
            key={brand.brandId}
            brand={brand}
            clientTotal={client.total}
            scale={brandScale}
          />
        ))}
      </Div>
    </Modal>
  );
};
